/**
 * The instant an event ends: the first instant of the day after its
 * `endDate`, on the event's own clock. That is midnight after `endDate`,
 * except in a zone whose clocks skip midnight itself for daylight saving
 * (Atlantic/Azores, America/Santiago), where the next day begins at 01:00.
 * New table requests close then (TNG-92, decision 3), and the "After the
 * event" section opens (TNG-96).
 *
 * Rules cannot do this sum, having no time zones, and admit a request until
 * that date has ended everywhere on Earth; the page is what holds the exact
 * line, by hiding the request button from this instant on.
 */

const dayFormats = new Map<string, Intl.DateTimeFormat>();

/** The calendar date an instant falls on in `timeZone`, as YYYY-MM-DD. */
function localDate(timeZone: string, instant: number): string {
  let format = dayFormats.get(timeZone);
  if (!format) {
    format = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    dayFormats.set(timeZone, format);
  }
  // Assembled from the parts rather than read off the formatted string, so
  // the comparison below never depends on how a locale lays a date out.
  const parts = new Map(
    format.formatToParts(instant).map((part) => [part.type, part.value]),
  );
  return `${parts.get("year")}-${parts.get("month")}-${parts.get("day")}`;
}

const HOUR = 3_600_000;

export function eventEndsAt(endDate: string, timeZone: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(endDate);
  if (!match) return null;
  const [, year, month, day] = match;
  // Midnight after endDate as if the zone were UTC. Every zone's offset is
  // within 14 hours of that, so the day after endDate starts somewhere in
  // the 30 hours either side.
  const wall = Date.UTC(Number(year), Number(month) - 1, Number(day) + 1);
  const nextDay = new Date(wall).toISOString().slice(0, 10);
  try {
    // The local date only ever moves forward with the instant, so the first
    // instant on `nextDay` is found by bisection, to the millisecond.
    let before = wall - 30 * HOUR;
    let from = wall + 30 * HOUR;
    if (localDate(timeZone, before) >= nextDay) return null;
    while (from - before > 1) {
      const middle = Math.floor((before + from) / 2);
      if (localDate(timeZone, middle) >= nextDay) from = middle;
      else before = middle;
    }
    return new Date(from);
  } catch {
    return null;
  }
}

export function eventHasEnded(
  endDate: string,
  timeZone: string,
  now: Date = new Date(),
): boolean {
  const end = eventEndsAt(endDate, timeZone);
  return end !== null && now.getTime() >= end.getTime();
}
